/**
 * The shared categorical tone map (#747).
 *
 * `trfer-display.ts` and `circle-display.ts` carried near-duplicate
 * `TONE_CLASSES` tables — same five keys, same token families — and #720 had
 * to fix the identical dark-mode defect in both, one after the other. This is
 * the single source both now extend, so "fixed in one, missed in the other"
 * stops being a class of bug.
 *
 * Built on `StatusChip`'s `ChipTone` vocabulary rather than beside it: the
 * `tone` field points into the canonical chip-triple table
 * (`StatusChip.tsx`), and chips render `<StatusChip tone={…}>` — never a
 * hand-assembled `pillBg` + `text` pair. The class fields here exist for
 * avatars and washes, which are not chips.
 *
 * No `dot`/`rail` fields on the base rows: a dot is a small mark, not an
 * avatar wash, so it takes the strong `-fg` from `TONE_DOT` below. Circle used
 * to paint its relationship dots with `avatarBg` (the soft tint) and kept that
 * asymmetry on purpose (#747), until the E2E campaign measured the dark tint
 * at oklab L 0.26 on an L 0.27 chip: invisible (#2423). Both extensions now
 * read the same strong marks.
 */
// Type-only: erased at build, so this stays a data module with no component dep.
import type { ChipTone } from '@/components/editorial/StatusChip';

/** The keys stay named after hues because ~40 call sites switch on them, but
 *  they resolve to token families, never raw hues (design-grammar §3, #719). */
export type ToneKey = 'rose' | 'amber' | 'sky' | 'green' | 'indigo';

export interface BaseToneClasses {
  /** soft tint for the avatar / icon chip */
  avatarBg: string;
  /** strong text color for the icon + pill label */
  text: string;
  /** pill background tint */
  pillBg: string;
  /**
   * The `StatusChip` tone this key means. Chips render `<StatusChip tone={…}>`
   * rather than assembling `pillBg` + `text` by hand.
   */
  tone: ChipTone;
}

export const BASE_TONE_CLASSES: Record<ToneKey, BaseToneClasses> = {
  rose:   { avatarBg: 'bg-status-danger', text: 'text-status-danger-fg', pillBg: 'bg-status-danger', tone: 'danger' },
  amber:  { avatarBg: 'bg-status-warn',   text: 'text-status-warn-fg',   pillBg: 'bg-status-warn',   tone: 'warn'   },
  sky:    { avatarBg: 'bg-cat-sky',       text: 'text-cat-sky-fg',       pillBg: 'bg-cat-sky',       tone: 'sky'    },
  green:  { avatarBg: 'bg-status-ok',     text: 'text-status-ok-fg',     pillBg: 'bg-status-ok',     tone: 'ok'     },
  // `indigo` means "no signal". Its avatar wash keeps the brand hue because it
  // is an identity mark, not a chip; the CHIP tone is neutral, because grammar
  // §3 is explicit that a badge is never indigo — indigo means interactive.
  indigo: { avatarBg: 'bg-stevens-indigo/10', text: 'text-stevens-indigo', pillBg: 'bg-chip-neutral', tone: 'neutral' },
};

/**
 * The strong mark for each tone: picker dots and rails. The `-fg` strength,
 * because a 7px dot has no area to carry a soft tint (#2423 measured circle's
 * tinted dots invisible in dark mode). `indigo` keeps the brand hue: a dot is
 * an identity mark, not a chip.
 */
export const TONE_DOT: Record<ToneKey, string> = {
  rose:   'bg-status-danger-fg',
  amber:  'bg-status-warn-fg',
  sky:    'bg-cat-sky-fg',
  green:  'bg-status-ok-fg',
  indigo: 'bg-stevens-indigo',
};

// ── Role identity (#762) ────────────────────────────────────────────────────
//
// Before #762 the platform carried FOUR role→colour vocabularies
// (admin/types.ts, TranslationReportsPage, AdminActivityPage, and the sidebar
// dots), two of which put roles in STATUS hues — a parent rendered ok-green
// and the retired helper role rendered warn-amber, the exact "role wearing a
// status" failure design-grammar §3 names. Roles are kinds, so they take
// categorical tones, once, here. `helper` is gone (F-009) and gets no row.

export type PlatformRole = 'admin' | 'trfer' | 'parent' | 'applicant';

/** Chips render `<StatusChip tone={ROLE_CHIP_TONE[role]}>`. */
export const ROLE_CHIP_TONE: Record<PlatformRole, ChipTone> = {
  admin:     'neutral',
  trfer:     'violet',
  parent:    'sky',
  applicant: 'teal',
};

/** The matching strong dot for identity marks (sidebar profile row, activity
 *  feeds) — same family as the chip tone, `-fg` strength. */
export const ROLE_DOT: Record<PlatformRole, string> = {
  admin:     'bg-chip-neutral-fg',
  trfer:     'bg-cat-violet-fg',
  parent:    'bg-cat-sky-fg',
  applicant: 'bg-cat-teal-fg',
};

/** Role → chip tone with the safe fallback for unknown/legacy role strings
 *  (e.g. a record predating F-009's helper removal): neutral, never a status
 *  hue. Use this at call sites rather than indexing `ROLE_CHIP_TONE` raw. */
export function roleChipTone(role: string | null | undefined): ChipTone {
  return ROLE_CHIP_TONE[(role ?? '') as PlatformRole] ?? 'neutral';
}
